import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
    FormControl,
    InputLabel,
    InputAdornment,
    IconButton,
    OutlinedInput,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';

type SearchBarProps = {
    width: 'sm' | 'md' | 'lg' | 'full-width';
    onChange: (value: string) => void;
    placeholder?: string;
    autofocus?: boolean;
    canClear?: boolean;
    label?: string;
    testId?: string;
    disabled?: boolean;
    resetTrigger?: boolean;
};

const SearchBar = ({
    width,
    onChange,
    placeholder = '',
    autofocus = false,
    canClear = true,
    label = undefined,
    testId = undefined,
    disabled = false,
    resetTrigger = false,
}: SearchBarProps) => {
    const widthStyles: Record<string, string | number> = {
        sm: 180,
        md: 300,
        lg: 420,
        'full-width': '100%',
    };

    const [value, setValue] = useState('');
    useEffect(() => {
        if (resetTrigger) setValue('');
    }, [resetTrigger]);

    const debouncedSearch = React.useMemo(
        () => _.debounce((search) => onChange(search), 500),
        [onChange],
    );

    const handleClear = () => {
        setValue('');
        onChange('');
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setValue(newValue);
        debouncedSearch(newValue);
    };

    return (
        <FormControl sx={{ width: widthStyles[width], borderRadius: '10px' }}>
            {label && (
                <InputLabel htmlFor="input-with-icon-adornment">
                    {label}
                </InputLabel>
            )}
            <OutlinedInput
                id="input-with-icon-adornment"
                placeholder={placeholder}
                autoFocus={autofocus}
                value={value}
                onChange={handleChange}
                label={label || undefined}
                sx={{ height: '40px' }}
                startAdornment={
                    <InputAdornment position="start">
                        <SearchIcon />
                    </InputAdornment>
                }
                disabled={disabled}
                endAdornment={
                    canClear &&
                    value && (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="clear search"
                                onClick={handleClear}
                            >
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }
                data-testid={testId}
            />
        </FormControl>
    );
};

export default SearchBar;
